import { cloneElement, createElement, Fragment, isValidElement, memo, PureComponent } from 'react'

import classNames from 'clsx'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { omit } from 'ramda'

import { Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'

import { captureException as captureSentryException } from '@sentry/react'

import config from '~/src/App/config'
import { MOBILE_LAYOUT_MEDIA_QUERY } from '~/src/App/styles'
import getGlobal from '~/src/Lib/getGlobal'
import { captureLogRocketException } from '~/src/Lib/LogRocket'
import { atLeastOneRequired } from '~/src/Lib/PropTypes'
import { defer, uniqueId } from '~/src/Lib/Utils'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import getClassPrefixer from '~/src/UI/classPrefixer'
import CloseButton from '~/src/UI/Shared/CloseButton'
import SomethingWentWrong from '~/src/UI/Shared/SomethingWentWrong'

const dialogOmitProps = [
  'autoOverflow',
  'content',
  'contentRight',
  'actions',
  'title',
  'closeButton',
  'classes',
  'dialog',
  'doOpenDialog',
  'doCloseDialog',
  'disableBackdropClick',
  'disableEscapeKeyDown',
  'dense',
  'isMobile',
  'splitActions',
  'titleProps',
  'transparent',
  'withErrorBoundary',
  'Wrapper',
  'TransitionProps',
]
const wrapperOmitProps = [
  ...dialogOmitProps,
  'fullScreen',
  'fullWidth',
  'open',
  'maxWidth',
  'PaperProps',
  'slotProps',
  'sx',
  'TransitionProps',
  'Wrapper',
]

const getMuiProps = memoizeOne(omit(dialogOmitProps))
const wrapperOmitter = omit(wrapperOmitProps)
const getWrapperProps = memoizeOne((classes, passthru, wrapperProps) => ({
  ...wrapperOmitter(passthru),
  ...wrapperProps,
  className: classNames(classes.wrapper, wrapperProps?.className),
}))

const getActionsClasses = memoizeOne(({ actions: root, actionsSpacing: spacing }) => ({
  root,
  spacing,
}))
const getContentClasses = memoizeOne((classes, { dense, classes: propClasses }, { overflowX }) => ({
  root: classNames({
    [classes.content]: true,
    [classes.dense]: dense,
    [propClasses?.content]: true,
    'scrollbar-x': overflowX,
  }),
}))

const getTitleClasses = memoizeOne((classes, { dense }) => ({
  root: classNames({
    [classes.title]: true,
    [classes.dense]: dense,
  }),
}))

const mergePaperSlotProps = (slotProps, paperProps) => {
  if (!paperProps) return slotProps

  const { paper: existingPaper, ...restSlotProps } = slotProps ?? {}
  return {
    ...restSlotProps,
    paper: {
      ...existingPaper,
      ...paperProps,
      className: classNames(existingPaper?.className, paperProps.className),
      style: {
        ...existingPaper?.style,
        ...paperProps.style,
      },
      sx: [existingPaper?.sx, paperProps.sx].filter(Boolean),
    },
  }
}

const mergeTransitionSlotProps = (slotProps, transitionProps) => {
  if (!transitionProps) return slotProps

  const { transition: existingTransition, ...restSlotProps } = slotProps ?? {}

  if (typeof existingTransition === 'function') {
    return {
      ...restSlotProps,
      transition: ownerState => {
        const resolvedTransition = existingTransition(ownerState) ?? {}
        return {
          ...resolvedTransition,
          ...transitionProps,
          onEntering: (...args) => {
            transitionProps.onEntering?.(...args)
            resolvedTransition.onEntering?.(...args)
          },
          onExiting: (...args) => {
            transitionProps.onExiting?.(...args)
            resolvedTransition.onExiting?.(...args)
          },
        }
      },
    }
  }

  return {
    ...restSlotProps,
    transition: {
      ...existingTransition,
      ...transitionProps,
      onEntering: (...args) => {
        transitionProps.onEntering?.(...args)
        existingTransition?.onEntering?.(...args)
      },
      onExiting: (...args) => {
        transitionProps.onExiting?.(...args)
        existingTransition?.onExiting?.(...args)
      },
    },
  }
}

const displayName = 'Dialog'
const classes = getClassPrefixer(displayName)
export const mobileActionsSx = {
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  // Anchors too: a Button given an `href` renders as one, and dialog actions that
  // route to another dialog by hash are the common case, not the exception.
  '& button, & a': {
    marginLeft: '0 !important',
    marginRight: '0 !important',
    width: '100%',
  },
}
const Root = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1ch',
    [theme.breakpoints.up('md')]: {
      padding: 'clamp(1rem, 4.5vh, 3rem) clamp(1.25rem, 3.3vw, 4rem)',
      gap: '2rem',
    },
  },
  [`& .${classes.root}.${classes.contentRight}`]: {
    display: 'grid',
    gridTemplateAreas: [
      '"dialogTitle dialogTitle"',
      '"dialogContent dialogContentRight"',
      '"dialogActions dialogActions"',
    ].join('\n'),
    gridTemplateColumns: '1fr clamp(20rem, 25vw, 30rem)',
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
    gap: '2rem 1rem',
    minWidth: 'clamp(1024px, 75vw, 1280px)',
  },
  [`& .${classes.root}.${classes.contentRight}.${classes.noActions}`]: {
    gridTemplateAreas: ['"dialogTitle dialogTitle"', '"dialogContent dialogContentRight"'].join('\n'),
  },
  [`& .${classes.root}.${classes.contentRight}.${classes.noTitle}`]: {
    gridTemplateAreas: ['"dialogContent dialogContentRight"', '"dialogActions dialogActions"'].join('\n'),
  },
  [`& .${classes.root}.${classes.contentRight}.${classes.noActions}.${classes.noTitle}`]: {
    gridTemplateAreas: ['"dialogContent dialogContentRight"'].join('\n'),
  },
  [`& .${classes.root}.MuiDialog-paperFullScreen .${classes.content}`]: {
    marginRight: 0,
    [theme.breakpoints.up('md')]: {
      paddingRight: '1rem',
      paddingLeft: '1rem',
    },
  },
  [`& .${classes.root}.${classes.overflowY}`]: {
    padding: [
      'clamp(1rem, 4.5vh, 3rem)',
      'calc(clamp(1.25rem, 3.3vw, 4rem) - 6px)',
      'clamp(1rem, 4.5vh, 3rem)',
      'clamp(1.25rem, 3.3vw, 4rem)',
    ].join(' '),
  },
  [`& .${classes.root}.MuiDialog-paperFullScreen`]: {
    paddingTop: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(2)})`,
    paddingRight: `max(env(safe-area-inset-right, 0px), ${theme.spacing(2)})`,
    paddingBottom: `max(env(safe-area-inset-bottom, 0px), ${theme.spacing(2)})`,
    paddingLeft: `max(env(safe-area-inset-left, 0px), ${theme.spacing(2)})`,
  },
  [`& .${classes.root}.MuiDialog-paperFullScreen.${classes.noTitle}`]: {
    paddingTop: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(7)})`,
  },
  [`& .${classes.root}.MuiDialog-paperFullScreen [data-testid='aroya-close-button']`]: {
    top: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(0.5)})`,
    right: `max(env(safe-area-inset-right, 0px), ${theme.spacing(0.5)})`,
  },
  [`& .${classes.actions}`]: {
    padding: 0,
    gap: '1rem',
    [theme.breakpoints.down('sm')]: {
      ...mobileActionsSx,
    },
  },
  [`& .${classes.transparent}`]: {
    background: 'transparent',
  },
  [`& .${classes.wrapper}`]: {
    display: 'contents',
  },
  [`& .${classes.root}.${classes.contentRight} .${classes.wrapper} .${classes.content}`]: {
    gridArea: 'dialogContent',
    maxHeight: 'calc(100dvh - 18rem)',
  },
  [`& .${classes.root}.${classes.contentRight} .${classes.wrapper} .${classes.contentRightCol}`]: {
    gridArea: 'dialogContentRight',
  },
  [`& .${classes.root}.${classes.contentRight} .${classes.wrapper} .${classes.title}`]: {
    gridArea: 'dialogTitle',
  },
  [`& .${classes.root}.${classes.contentRight} .${classes.wrapper} .${classes.actions}`]: {
    gridArea: 'dialogActions',
  },
  [`& .${classes.contentRightCol}`]: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    background: theme.palette.background.paper,
  },
  [`& .${classes.content}`]: {
    overflowY: 'auto',
    overflowX: 'hidden',
    '&.MuiDialogContent-root': {
      padding: 0,
    },
    [`&.${classes.noActions}`]: {
      paddingBottom: theme.spacing(2),
    },
    '&:empty': {
      display: 'none',
    },
    '&:first-of-type': {
      paddingTop: 0,
      '& .MuiFormControl-root:first-of-type': {
        marginTop: 0,
      },
    },
  },
  [`& .${classes.root}.${classes.overflowY} .${classes.content}`]: {
    paddingRight: '0.5rem',
  },
  [`& .${classes.title}`]: {
    padding: 0,
  },
  [`& .${classes.title}.${classes.dense}`]: {
    padding: theme.spacing(1, 0.5),
  },
  [`& .${classes.actionsSplit}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.errorIcon}`]: {
    color: theme.palette.warning.main,
  },
}))

const resetError = () => {
  getGlobal().location.reload()
}
export class DialogComponent extends PureComponent {
  static displayName = displayName

  static propTypes = {
    actions: PropTypes.node,
    autoOverflow: PropTypes.bool,
    contentRight: PropTypes.node,
    dense: PropTypes.bool,
    titleProps: PropTypes.shape({
      variant: PropTypes.string,
      color: PropTypes.string,
      sx: PropTypes.shape({}),
    }),
    withErrorBoundary: PropTypes.bool,
    Wrapper: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
    doOpenDialog: PropTypes.func.isRequired,
    doCloseDialog: PropTypes.func.isRequired,
    ...atLeastOneRequired({
      children: PropTypes.node,
      content: PropTypes.node,
      title: PropTypes.node,
    }),
  }

  static defaultProps = {
    actions: null,
    autoOverflow: false,
    contentRight: null,
    dense: false,
    titleProps: {},
    withErrorBoundary: true,
    Wrapper: 'div',
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  state = {
    hasError: false,
    overflowX: false,
    overflowY: false,
  }

  contentRef = null

  constructor(props) {
    super(props)
    this.idPrefix = uniqueId('dialog-')
  }

  componentDidMount() {
    if (this.props.autoOverflow) {
      defer(() => {
        const { contentRef: content } = this
        if (!content) return

        if (content.offsetWidth < content.scrollWidth) {
          this.setState({ overflowX: true })
        }
      }, defer.priorities.low)
    }
    defer(() => {
      const { contentRef: content } = this
      if (!content) return

      if (content.offsetHeight < content.scrollHeight) {
        this.setState({ overflowY: true })
      }
    }, defer.priorities.low)
  }

  componentDidCatch(error, info) {
    if (this.props.withErrorBoundary) {
      console.error('[Dialog]', { error, info })
      if (config.SENTRY_DSN) {
        captureSentryException(error, { extra: info })
      }
      captureLogRocketException(error, { extra: { componentStack: info.componentStack } })
    } else {
      throw error
    }
  }

  componentWillUnmount() {
    this.props.doCloseDialog()
  }

  get dialogProps() {
    const muiProps = getMuiProps(this.props)
    const { PaperProps, slotProps, ...dialogProps } = muiProps
    const mergedSlotProps = mergeTransitionSlotProps(mergePaperSlotProps(slotProps, PaperProps), {
      onEntering: () => {
        this.props.doOpenDialog()
      },
      onExiting: () => {
        this.props.doCloseDialog()
      },
    })

    if ('title' in this.props && this.props.title) {
      return {
        ...dialogProps,
        slotProps: mergedSlotProps,
        'aria-labelledby': this.titleId,
      }
    }

    return {
      ...dialogProps,
      slotProps: mergedSlotProps,
    }
  }

  get titleId() {
    const { title } = this.props
    if (!title) return null
    return `${this.idPrefix}-title`
  }

  setContentRef = el => {
    this.contentRef = el
  }

  render() {
    const {
      actions,
      children,
      closeButton,
      content = children,
      contentRight,
      isMobile,
      splitActions,
      title,
      titleProps,
      transparent,
      withErrorBoundary,
      Wrapper,
      onClose,
      disableBackdropClick: _disableBackdropClick,
      disableEscapeKeyDown: _disableEscapeKeyDown,
      ...passthru
    } = this.props
    const { overflowY } = this.state
    const innerContent = [
      withErrorBoundary && this.state.hasError ? (
        <SomethingWentWrong
          key="dialog-error"
          classes={classes}
          message={this.state.errorMessage}
          onClick={resetError}
        />
      ) : (
        <Fragment key="dialog-inner">
          {title ? (
            <DialogTitle
              {...titleProps}
              classes={getTitleClasses(classes, this.props)}
              data-testid="dialog-title"
              id={this.titleId}
            >
              {title}
            </DialogTitle>
          ) : null}
          <DialogContent
            ref={this.setContentRef}
            classes={getContentClasses(classes, this.props, this.state)}
            data-testid="dialog-content"
          >
            {content}
            {children && children !== content ? children : null}
          </DialogContent>
          {actions ? (
            <DialogActions
              data-testid="dialog-actions"
              classes={getActionsClasses(classes)}
              className={splitActions ? classes.actionsSplit : undefined}
            >
              {actions}
            </DialogActions>
          ) : null}
          {(isMobile || closeButton) && onClose ? <CloseButton noMargin onClose={onClose} /> : null}
        </Fragment>
      ),
      contentRight ? (
        <Fragment key="dialog-contentRight">
          <div className={classes.contentRightCol}>{contentRight}</div>
          <CloseButton onClose={onClose} />
        </Fragment>
      ) : null,
    ]

    const wrapperProps = getWrapperProps(classes, passthru, Wrapper.props)

    return (
      <Root
        classes={{
          paper: classNames({
            [classes.root]: true,
            [classes.contentRight]: !!contentRight,
            [classes.transparent]: transparent,
            [classes.overflowY]: overflowY,
            [classes.noActions]: !actions,
            [classes.noTitle]: !title,
          }),
        }}
        data-testid="dialog"
        data-time-series__cursor-exclude
        fullScreen={!!isMobile}
        {...this.dialogProps}
        container={() => document.getElementById('modalRoot')}
      >
        {isValidElement(Wrapper)
          ? cloneElement(Wrapper, wrapperProps, innerContent)
          : createElement(Wrapper, wrapperProps, innerContent)}
      </Root>
    )
  }
}

/**
 * A Material-UI dialog with Aroya defaults and enhancements added
 *
 * @example
 *   import { Form, Formik, Field } from 'formik'
 *   import { FormikTextField } from '~/src/UI/Shared/Form/TextField'
 *   import Button from '~/src/UI/Shared/Button'
 *   {
 *     ;() => {
 *       const [submitted, setSubmitted] = React.useState(null)
 *       const [open, setOpen] = React.useState(false)
 *       const toggleOpen = React.useCallback(() => setOpen(old => !old), [setOpen])
 *       return (
 *         <Box className="default-display">
 *           <Button onClick={toggleOpen}>{open ? 'Close' : 'Open'} Dialog</Button>
 *           {submitted ? (
 *             <>
 *               <T>Submitted</T>
 *               <T.Tiny gutterBottom>
 *                 <code>{JSON.stringify(submitted, null, 2)}</code>
 *               </T.Tiny>
 *             </>
 *           ) : null}
 *           <Formik
 *             initialValues={{ text: 'foo' }}
 *             onSubmit={(data, { setSubmitting }) => {
 *               console.log('Dialog form data:', data)
 *               setTimeout(() => {
 *                 setSubmitted(data)
 *                 setSubmitting(false)
 *                 setOpen(false)
 *               }, 1000)
 *             }}
 *           >
 *             {formikProps => (
 *               <Dialog
 *                 open={open}
 *                 actions={
 *                   <Button disabled={formikProps.isSubmitting} type="submit">
 *                     Submit{formikProps.isSubmitting ? 'ing…' : null}
 *                   </Button>
 *                 }
 *                 content={<Field margin="normal" name="text" component={FormikTextField} />}
 *                 title="Test Form Dialog"
 *                 Wrapper={Form}
 *               />
 *             )}
 *           </Formik>
 *         </Box>
 *       )
 *     }
 *   }
 *
 * @component
 * @alias Dialog
 * @composes
 * from: '@mui/material/Dialog'
 * link: https://material-ui.com/api/dialog/#props
 */
const Wrapped = memo(props => {
  const { doOpenDialog, doCloseDialog } = useConnectModern('doOpenDialog', 'doCloseDialog')
  // The same query `selectIsMobile` reads, so callers that spread that selector agree with this.
  const isMobile = useMediaQuery(MOBILE_LAYOUT_MEDIA_QUERY)
  const connectedProps = {
    doOpenDialog,
    doCloseDialog,
  }

  // After the caller's props so the value computed here always wins.
  return <DialogComponent {...connectedProps} {...props} isMobile={isMobile} />
})

Wrapped.displayName = `ConnectedStyledMemoized(${displayName})`

export default Wrapped
